import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import useSeo from '../hooks/useSeo';
import { FiArrowLeft, FiArrowRight, FiEdit2 } from 'react-icons/fi';
import '../styles/pages/PublicCalendarBooking.css';

const DURATION_PRESETS = [15, 30, 45, 60];

// Utilities to work with availability payload
const pad2 = (n) => String(n).padStart(2, '0');
const toLocalYMD = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const dayKey = (date) => ['sun','mon','tue','wed','thu','fri','sat'][date.getDay()];

// Convert time "HH:MM" to minutes from 00:00
const toMinutes = (hhmm) => {
  const [h = '0', m = '0'] = String(hhmm || '0:0').split(':');
  return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
};

// Build time slots (30-minute granularity) from an array of ranges
const buildSlots = (ranges = [], stepMinutes = 30) => {
  const slots = [];
  (ranges || []).forEach((r) => {
    const start = toMinutes(r.start);
    const end = Math.max(start, toMinutes(r.end));
    for (let t = start; t + stepMinutes <= end; t += stepMinutes) {
      const h = Math.floor(t / 60), m = t % 60;
      slots.push(`${pad2(h)}:${pad2(m)}`);
    }
  });
  return slots;
};

const formatTime12 = (hhmm) => {
  const [h = '0', m = '0'] = String(hhmm || '0:0').split(':');
  let hh = parseInt(h, 10) || 0;
  const mm = pad2(parseInt(m, 10) || 0);
  const period = hh < 12 ? 'AM' : 'PM';
  const disp = hh % 12 === 0 ? 12 : (hh % 12);
  return `${disp}:${mm} ${period}`;
};

// Add minutes to "HH:MM" and return "HH:MM"
const addMinutesToHHMM = (hhmm, add) => {
  const [h = '0', m = '0'] = String(hhmm || '0:0').split(':');
  const total = (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0) + (parseInt(add, 10) || 0);
  const norm = ((total % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hh = Math.floor(norm / 60);
  const mm = norm % 60;
  return `${pad2(hh)}:${pad2(mm)}`;
};

export default function PublicCalendarBooking() {
  const { slug } = useParams();
  const seoConfig = useMemo(() => {
    const title = 'Book a meeting | BloomNav';
    const description =
      'Book a meeting using a BloomNav calendar link. Select an available slot and submit your details.';
    return {
      title,
      description,
      canonicalPath: `/calendar/${encodeURIComponent(slug || '')}`,
      ogType: 'website',
      ogImage: '/logo.png',
      ogImageAlt: 'BloomNav',
      robots: 'noindex, follow'
    };
  }, [slug]);

  useSeo(seoConfig);

  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date(); d.setHours(0,0,0,0); return d;
  });
  const [selectedTime, setSelectedTime] = useState('');
  const [meetingDuration, setMeetingDuration] = useState(30);
  const [customDurationOpen, setCustomDurationOpen] = useState(false);
  const [showExtras, setShowExtras] = useState(false);
  const [step, setStep] = useState(1); // 1 date & duration · 2 time · 3 details/confirm
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [agenda, setAgenda] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authName, setAuthName] = useState('');
  const isSignedIn = useMemo(() => !!authEmail || !!email, [authEmail, email]);
  const [resolvedAddress, setResolvedAddress] = useState('');
  const [pin, setPin] = useState(null);
  const [locQuery, setLocQuery] = useState('');
  const [locSuggestions, setLocSuggestions] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [hostName, setHostName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [supervisorId, setSupervisorId] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [referenceId, setReferenceId] = useState(null);
  const [submittedSummary, setSubmittedSummary] = useState(null);
  const [meetingLink, setMeetingLink] = useState('');
  // Image upload state
  const [images, setImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // "Now" helpers (local time)
  const now = new Date();
  const todayYmd = toLocalYMD(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const todayMidnight = useMemo(() => {
    const t = new Date(); t.setHours(0,0,0,0); return t;
  }, []);
  // companyId from backend response
  const [companyId, setCompanyId] = useState('');

  // Month identity so we only refetch when month changes
  const monthId = useMemo(() => (selectedDate.getFullYear() * 12) + selectedDate.getMonth(), [selectedDate]);
  // Month calendar (6-week grid)
  const monthAnchor = useMemo(() => {
    const y = Math.floor(monthId / 12);
    const m = monthId % 12;
    const d = new Date(y, m, 1);
    d.setHours(0,0,0,0);
    return d;
  }, [monthId]);

  // Helper: pick ranges for a given date (specific overrides generic; supports everyYear)
  const rangesForDate = useMemo(() => {
    return (date) => {
      if (!availability) return [];
      const schedule = availability.schedule || availability; // support both shapes
      const ymd = toLocalYMD(date);
      const mmdd = ymd.slice(5);
      const specific = (schedule.specific || []).find((s) => String(s.date) === ymd)
        || (schedule.specific || []).find((s) => s.everyYear && String(s.date || '').slice(5) === mmdd);
      if (specific) return Array.isArray(specific.slots) ? specific.slots : [];
      const generic = (schedule.generic || {})[dayKey(date)] || [];
      return generic;
    };
  }, [availability]);

  // Map meetings by local date ymd for quick lookup
  const meetingsByYmd = useMemo(() => {
    const map = {};
    (meetings || []).forEach((m) => {
      const d = new Date(m.dueDate);
      const ymd = toLocalYMD(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
      if (!map[ymd]) map[ymd] = [];
      map[ymd].push({
        startMinutes: d.getHours() * 60 + d.getMinutes(),
        duration: Number.isFinite(m.duration) && m.duration > 0 ? m.duration : 30
      });
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.startMinutes - b.startMinutes));
    return map;
  }, [meetings]);

  // Generate available start times within ranges for the given duration and existing meetings
  const generateAvailableStarts = useMemo(() => {
    return (ranges, duration, dayMeetings, stepMinutes = 15) => {
      const result = [];
      const normalizedRanges = (ranges || []).map((r) => {
        const s = toMinutes(r.start);
        const e = Math.max(s, toMinutes(r.end));
        return { s, e };
      }).filter((r) => r.e > r.s);
      normalizedRanges.forEach((r) => {
        for (let t = r.s; t + duration <= r.e; t += stepMinutes) {
          const tEnd = t + duration;
          const overlaps = (dayMeetings || []).some((meet) => {
            const ms = meet.startMinutes;
            const me = meet.startMinutes + (meet.duration || 30);
            return ms < tEnd && me > t;
          });
          if (!overlaps) {
            const h = Math.floor(t / 60), m = t % 60;
            result.push(`${pad2(h)}:${pad2(m)}`);
          }
        }
      });
      return result;
    };
  }, []);
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (hasLoaded) setCalendarLoading(true);
      else setLoading(true);
      try {
        const start = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1, 0, 0, 0, 0);
        const end = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0, 23, 59, 59, 999);
        const { data } = await api.get(`/public/availability/${encodeURIComponent(slug)}`, {
          params: { start: start.toISOString(), end: end.toISOString() }
        });
        if (!mounted) return;
        if (data?.is_success && data.availability) {
          setAvailability(data.availability);
          setHasLoaded(true);
          if (data.companyId) setCompanyId(String(data.companyId));
          if (data.supervisorId) setSupervisorId(Number(data.supervisorId));
          if (data.departmentId !== undefined) setDepartmentId(data.departmentId === null ? null : Number(data.departmentId));
          // Merge meetings (internal) and google events into a single blocking set
          const internalMeetings = Array.isArray(data.meetings) ? data.meetings : [];
          const googleEvents = Array.isArray(data.googleEvents) ? data.googleEvents : [];
          const merged = [
            ...internalMeetings.map((m) => ({
              id: m.id,
              dueDate: m.dueDate,
              duration: Number.isFinite(m.duration) && m.duration > 0 ? m.duration : 30
            })),
            ...googleEvents.map((e) => {
              const start = new Date(e.start);
              const end = new Date(e.end);
              const dur = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
              return { id: `g-${e.id || start.toISOString()}`, dueDate: start.toISOString(), duration: dur || 30 };
            })
          ];
          setMeetings(merged);
          if (data.hostName) setHostName(data.hostName);
        } else {
          setError(data?.message || 'Not found');
        }
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load availability');
      } finally {
        if (mounted) {
          setLoading(false);
          setCalendarLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [slug, monthId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const apply = () => setIsMobile(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);

  const gridDays = useMemo(() => {
    const start = new Date(monthAnchor);
    start.setDate(start.getDate() - start.getDay());
    start.setHours(0,0,0,0);
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [monthAnchor]);

  // Autocomplete for location using Nominatim
  useEffect(() => {
    const q = String(locQuery || '').trim();
    if (!q || q.length < 3) { setLocSuggestions([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setLocLoading(true);
        const resp = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5`, {
          headers: { 'User-Agent': 'BloomNav' }
        });
        const data = await resp.json();
        if (!cancelled) {
          setLocSuggestions(Array.isArray(data) ? data : []);
        }
      } catch (_) {
        if (!cancelled) setLocSuggestions([]);
      } finally {
        if (!cancelled) setLocLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [locQuery]);

  const slotsForDate = useMemo(() => {
    if (!availability) return [];
    const ymd = toLocalYMD(selectedDate);
    const ranges = rangesForDate(selectedDate);
    const todaysMeetings = meetingsByYmd[ymd] || [];
    const duration = Number.isFinite(meetingDuration) && meetingDuration > 0 ? meetingDuration : 30;
    let slots = generateAvailableStarts(ranges, duration, todaysMeetings, 15);
    // Filter out times that are before "now" when the selected day is today
    if (ymd === todayYmd) {
      slots = slots.filter((s) => toMinutes(s) >= nowMinutes);
    }
    // No slots for past days
    const selMidnight = new Date(selectedDate); selMidnight.setHours(0,0,0,0);
    if (selMidnight.getTime() < todayMidnight.getTime()) {
      return [];
    }
    return slots;
  }, [availability, selectedDate, meetingsByYmd, meetingDuration, generateAvailableStarts, rangesForDate]);

  // Heatmap: slot counts + whether the day has scheduled hours
  const heatmapMeta = useMemo(() => {
    if (!availability || !gridDays?.length) return {};
    const duration = Number.isFinite(meetingDuration) && meetingDuration > 0 ? meetingDuration : 30;
    const meta = {};
    gridDays.forEach((d) => {
      const ymd = toLocalYMD(d);
      const ranges = rangesForDate(d);
      const hasSchedule = Array.isArray(ranges) && ranges.length > 0;
      const todaysMeetings = meetingsByYmd[ymd] || [];
      const dMidnight = new Date(d); dMidnight.setHours(0, 0, 0, 0);
      if (dMidnight.getTime() < todayMidnight.getTime()) {
        meta[ymd] = { count: 0, hasSchedule: false };
        return;
      }
      let available = generateAvailableStarts(ranges, duration, todaysMeetings, 15);
      if (ymd === todayYmd) {
        available = available.filter((s) => toMinutes(s) >= nowMinutes);
      }
      meta[ymd] = { count: available.length, hasSchedule };
    });
    return meta;
  }, [availability, gridDays, meetingsByYmd, meetingDuration, generateAvailableStarts, rangesForDate, todayMidnight, todayYmd, nowMinutes]);

  const heatmapCounts = useMemo(() => {
    const counts = {};
    Object.keys(heatmapMeta || {}).forEach((ymd) => {
      counts[ymd] = heatmapMeta[ymd]?.count || 0;
    });
    return counts;
  }, [heatmapMeta]);

  const heatmapMax = useMemo(() => {
    const vals = Object.values(heatmapMeta || {}).map((m) => m.count || 0);
    return vals.length ? Math.max(0, ...vals) : 0;
  }, [heatmapMeta]);

  const availabilityLevel = (ymd) => {
    const meta = heatmapMeta[ymd] || { count: 0, hasSchedule: false };
    const count = meta.count || 0;
    if (count > 0) {
      const relativeLow = heatmapMax > 0 && count / heatmapMax <= 0.35;
      return (count <= 3 || relativeLow) ? 'limited' : 'open';
    }
    if (meta.hasSchedule) return 'full';
    return 'none';
  };
  const findNextAvailableDate = (fromDate = selectedDate) => {
    const probe = new Date(fromDate);
    probe.setHours(0, 0, 0, 0);
    for (let i = 0; i < 90; i++) {
      const d = new Date(probe);
      d.setDate(probe.getDate() + i);
      if (d.getTime() < todayMidnight.getTime()) continue;
      if ((heatmapCounts[toLocalYMD(d)] || 0) > 0) return d;
    }
    return null;
  };

  const goToNextAvailable = () => {
    // Prefer a later day than the empty selection
    const start = new Date(selectedDate);
    start.setDate(start.getDate() + 1);
    const next = findNextAvailableDate(start) || findNextAvailableDate(todayMidnight);
    if (next) {
      setSelectedDate(next);
      setSelectedTime('');
      setToast(`Showing ${next.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}`);
    } else {
      setToast('No upcoming availability found');
    }
  };

  const isCustomDuration = customDurationOpen || !DURATION_PRESETS.includes(meetingDuration);

  const canConfirm = useMemo(() => {
    const effectiveEmail = (email || authEmail || '').trim();
    return !!(isSignedIn && effectiveEmail && selectedTime && phone);
  }, [isSignedIn, email, authEmail, selectedTime, phone]);

  const selectDuration = (mins) => {
    setMeetingDuration(mins);
    setCustomDurationOpen(false);
    setSelectedTime('');
  };

  const goToStep = (next) => {
    if (next === 2 && !selectedDate) {
      setToast('Pick a date first');
      return;
    }
    if (next === 3 && !selectedTime) {
      setToast('Pick a time first');
      return;
    }
    setStep(next);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const ctaMain = submitting
    ? 'Scheduling…'
    : (hostName ? `Confirm with ${hostName.split(' ')[0]}` : 'Confirm booking');
  const ctaSub = selectedTime
    ? `${selectedDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} · ${formatTime12(selectedTime)} · ${Number.isFinite(meetingDuration) && meetingDuration > 0 ? meetingDuration : 30} min`
    : 'Select a date and time to continue';

  const confirmBooking = async () => {
    setSubmitError('');
    setShowErrors(true);
    if (!isSignedIn || !phone || !selectedTime) {
      if (!isSignedIn) setToast('Please sign in to continue');
      else if (!phone) setToast('Please enter your phone number');
      else if (!selectedTime) setToast('Please select a time');
      else setToast('Please complete required fields');
      return;
    }
    try {
      setSubmitting(true);
      // Pre-flight availability check for the selected day/time
      try {
        const dayStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0, 0);
        const dayEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59, 999);
        const { data: checkData } = await api.get(`/public/availability/${encodeURIComponent(slug)}`, {
          params: { start: dayStart.toISOString(), end: dayEnd.toISOString() }
        });
        if (checkData?.is_success && checkData.availability) {
          const checkAvailability = checkData.availability;
          const internalMeetings = Array.isArray(checkData.meetings) ? checkData.meetings : [];
          const googleEvents = Array.isArray(checkData.googleEvents) ? checkData.googleEvents : [];
          const merged = [
            ...internalMeetings.map((m) => ({
              id: m.id,
              dueDate: m.dueDate,
              duration: Number.isFinite(m.duration) && m.duration > 0 ? m.duration : 30
            })),
            ...googleEvents.map((e) => {
              const s = new Date(e.start);
              const eDt = new Date(e.end);
              const dur = Math.max(0, Math.round((eDt.getTime() - s.getTime()) / 60000));
              return { id: `g-${e.id || s.toISOString()}`, dueDate: s.toISOString(), duration: dur || 30 };
            })
          ];
          // Build meetingsByYmd for the specific day
          const ymd = toLocalYMD(selectedDate);
          const dayMeetings = [];
          merged.forEach((m) => {
            const d = new Date(m.dueDate);
            const k = toLocalYMD(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
            if (k === ymd) {
              dayMeetings.push({
                startMinutes: d.getHours() * 60 + d.getMinutes(),
                duration: Number.isFinite(m.duration) && m.duration > 0 ? m.duration : 30
              });
            }
          });
          // Compute ranges for selected date from the fresh availability
          const schedule = checkAvailability.schedule || checkAvailability;
          const mmdd = ymd.slice(5);
          const specific = (schedule.specific || []).find((s) => String(s.date) === ymd)
            || (schedule.specific || []).find((s) => s.everyYear && String(s.date || '').slice(5) === mmdd);
          const ranges = specific ? (Array.isArray(specific.slots) ? specific.slots : []) : ((schedule.generic || {})[dayKey(selectedDate)] || []);
          const duration = Number.isFinite(meetingDuration) && meetingDuration > 0 ? meetingDuration : 30;
          let freshSlots = generateAvailableStarts(ranges, duration, dayMeetings, 15);
          // If today, only allow future times
          if (toLocalYMD(selectedDate) === todayYmd) {
            freshSlots = freshSlots.filter((s) => toMinutes(s) >= nowMinutes);
          }
          if (!freshSlots.includes(selectedTime)) {
            setSubmitting(false);
            setToast('Selected time is no longer available. Please pick another slot.');
            return;
          }
        }
      } catch (_) {
        // If the check fails, proceed with best effort
      }
      // Build complaint submit payload (meeting task) and send via public submit API
      const tz = availability?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const start = selectedTime;
      const end = addMinutesToHHMM(selectedTime, Number.isFinite(meetingDuration) && meetingDuration > 0 ? meetingDuration : 30);
      const [sh = '0', sm = '0'] = String(start || '0:0').split(':');
      const dueStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), parseInt(sh, 10) || 0, parseInt(sm, 10) || 0, 0, 0);
      const durationMins = Number.isFinite(meetingDuration) && meetingDuration > 0 ? meetingDuration : 30;
      // Require companyId from backend
      if (!companyId) {
        setToast('Missing company. Please try again later.');
        setSubmitting(false);
        return;
      }
      // Create or fetch customer to obtain customerId
      let customerId = null;
      const effectiveEmail = (email || authEmail || '').trim();
      const effectiveName = (name || authName || '').trim();
      const effectivePhone = (phone || '').trim();
      try {
        // Try to get existing customer by email
        const existing = await api.get('/api/customers', { params: { email: effectiveEmail, companyId } });
        if (existing?.data?.is_success && existing?.data?.customer?.id) {
          customerId = existing.data.customer.id;
        }
      } catch (_) { /* ignore */ }
      if (!customerId) {
        try {
          const created = await api.post('/api/customers', { name: effectiveName, email: effectiveEmail, phone: effectivePhone, companyId });
          if (created?.data?.is_success && created?.data?.customer?.id) {
            customerId = created.data.customer.id;
          }
        } catch (_) { /* ignore */ }
      }
      if (!customerId) {
        setSubmitting(false);
        setToast('Could not create customer. Please try again.');
        return;
      }
      // Build location object similar to ComplaintForm
      const locObj = (() => {
        const addr = (resolvedAddress || location || '').trim();
        if (pin && Number.isFinite(pin.lat) && Number.isFinite(pin.lng)) {
          return { address: addr, coordinates: { lat: pin.lat, lng: pin.lng } };
        }
        return addr || '' ;
      })();
      const complaintData = {
        companyId,
        customerId,
        description: (agenda || '').trim() || `Meeting booked via calendar${hostName ? ` with ${hostName}` : ''}`,
        location: locObj,
        images: images || [],
        departmentId: Number.isFinite(Number(departmentId)) ? Number(departmentId) : null,
        priority: 'medium',
        taskType: 'meeting',
        dueDate: dueStart.toISOString(),
        followUpOn: null,
        meetingDuration: durationMins,
        supervisorId: Number.isFinite(Number(supervisorId)) ? Number(supervisorId) : undefined,
        timezone: tz
      };
      const complaintResponse = await api.post('/api/complaints/submit', complaintData);
      if (complaintResponse?.data?.is_success) {
        const refId = complaintResponse?.data?.complaint?.id || null;
        setReferenceId(refId);
        const link =
          complaintResponse?.data?.complaint?.meetingUrl ||
          complaintResponse?.data?.complaint?.meetingLink ||
          complaintResponse?.data?.meetingUrl ||
          complaintResponse?.data?.meetingLink ||
          '';
        if (link) setMeetingLink(link);
        setIsSubmitted(true);
        setSubmittedSummary({
          hostName,
          dateLabel: selectedDate.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }),
          timeLabel: `${formatTime12(start)} - ${formatTime12(end)}`,
          duration: durationMins,
          customerName: (name || authName || '').trim(),
          customerEmail: (email || authEmail || '').trim()
        });
      } else {
        const msg = complaintResponse?.data?.message || 'Failed to submit your request';
        setSubmitError(msg);
        setToast('Submission failed');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Network error while submitting';
      setSubmitError(msg);
      setToast('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential || '') || {};
      const e = (decoded.email || '').trim();
      const n = (decoded.name || '').trim();
      setAuthEmail(e);
      setAuthName(n);
      setEmail((prev) => prev || e);
      setName((prev) => prev || n);
      setToast('Signed in with Google');
    } catch {
      setToast('Google sign-in failed');
    }
  };

  if (loading && !hasLoaded) {
    return (
      <div className="pcb">
        <div className="pcb-hero pcb-skel" style={{ height: 84 }} />
        <div className="pcb-flow">
          <div className="pcb-card" aria-busy="true" aria-label="Loading availability">
            <div className="pcb-card-body">
              <div className="pcb-skel" style={{ height: 20, width: '40%', marginBottom: 16 }} />
              <div className="pcb-skel" style={{ height: 220, marginBottom: 16 }} />
              <div className="pcb-skel" style={{ height: 40, width: '70%', marginBottom: 16 }} />
              <div className="pcb-skel" style={{ height: 48 }} />
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (error && !hasLoaded) {
    return (
      <div className="pcb">
        <div className="pcb-card pcb-success">
          <div className="pcb-card-body pcb-error-state">
            <div className="pcb-error-icon" aria-hidden="true">!</div>
            <strong>This booking page could not be loaded</strong>
            <p>{error}</p>
            <button type="button" className="pcb-empty-btn" onClick={() => window.location.reload()}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="pcb">
        <div className="pcb-card pcb-success">
          <div className="pcb-card-head">
            {hostName ? `Meeting scheduled with ${hostName}` : 'Meeting scheduled'}
          </div>
          <div className="pcb-card-body">
            <div className="pcb-success-check" aria-hidden="true">✓</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--pcb-ink)', marginBottom: 6 }}>Request submitted</div>
            <div style={{ fontSize: 13, color: 'var(--pcb-muted)', marginBottom: 12 }}>We will review and confirm via email shortly.</div>
            {referenceId && (
              <div style={{ fontSize: 13, color: 'var(--pcb-muted)', margin: '2px 0 12px' }}>
                Reference ID: <span style={{ color: 'var(--pcb-ink)', fontWeight: 800 }}>#{referenceId}</span>
              </div>
            )}
            {!!meetingLink && (
              <div style={{ marginTop: 6, padding: 14, border: '1px solid var(--pcb-line)', borderRadius: 12, background: '#f8fafc', marginBottom: 12 }}>
                <div className="pcb-label">Meeting link</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <a href={meetingLink} target="_blank" rel="noreferrer" style={{ color: 'var(--pcb-accent)', fontWeight: 700, wordBreak: 'break-all', textDecoration: 'none' }}>{meetingLink}</a>
                  <button
                    type="button"
                    className="pcb-empty-btn"
                    onClick={() => { try { navigator.clipboard.writeText(meetingLink); setToast('Link copied'); } catch (_) {} }}
                    title="Copy meeting link"
                    aria-label="Copy meeting link"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
            {!!submittedSummary && (
              <div style={{ marginTop: 6, padding: 14, border: '1px solid var(--pcb-line)', borderRadius: 12, background: '#f8fafc' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  <div>
                    <div className="pcb-label">Date</div>
                    <div style={{ fontSize: 14, color: 'var(--pcb-ink)', fontWeight: 700 }}>{submittedSummary.dateLabel}</div>
                  </div>
                  <div>
                    <div className="pcb-label">Time</div>
                    <div style={{ fontSize: 14, color: 'var(--pcb-ink)', fontWeight: 700 }}>{submittedSummary.timeLabel}</div>
                  </div>
                  <div>
                    <div className="pcb-label">Duration</div>
                    <div style={{ fontSize: 14, color: 'var(--pcb-ink)' }}>{submittedSummary.duration} min</div>
                  </div>
                  {hostName && (
                    <div>
                      <div className="pcb-label">Host</div>
                      <div style={{ fontSize: 14, color: 'var(--pcb-ink)' }}>{hostName}</div>
                    </div>
                  )}
                  {submittedSummary.customerName && (
                    <div>
                      <div className="pcb-label">Booked by</div>
                      <div style={{ fontSize: 14, color: 'var(--pcb-ink)' }}>{submittedSummary.customerName}</div>
                    </div>
                  )}
                  {submittedSummary.customerEmail && (
                    <div>
                      <div className="pcb-label">Email</div>
                      <div style={{ fontSize: 14, color: 'var(--pcb-ink)' }}>{submittedSummary.customerEmail}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
              <button type="button" className="pcb-btn pcb-btn-primary" onClick={() => window.location.reload()}>
                Book another
              </button>
              <a href="/" className="pcb-btn pcb-btn-ghost" style={{ textDecoration: 'none' }}>
                Back to home
              </a>
            </div>
          </div>
        </div>
        <div className="pcb-footer">
          Powered by <a href="/" target="_blank" rel="noreferrer">BloomNav</a>
          <small>Unified Business Management</small>
        </div>
      </div>
    );
  }

  const durationMins = Number.isFinite(meetingDuration) && meetingDuration > 0 ? meetingDuration : 30;
  const monthLabel = monthAnchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const tzLabel = availability?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const calendarGrid = (
    <>
      <div className="pcb-month-nav">
        <button
          type="button"
          className="pcb-nav-icon"
          disabled={calendarLoading}
          aria-label="Previous month"
          onClick={() => {
            const d = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1);
            setSelectedDate(d);
            setSelectedTime('');
          }}
        >
          <FiArrowLeft size={18} strokeWidth={2.25} aria-hidden="true" />
        </button>
        <strong>{monthLabel}</strong>
        <button
          type="button"
          className="pcb-nav-icon"
          disabled={calendarLoading}
          aria-label="Next month"
          onClick={() => {
            const d = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1);
            setSelectedDate(d);
            setSelectedTime('');
          }}
        >
          <FiArrowRight size={18} strokeWidth={2.25} aria-hidden="true" />
        </button>
      </div>
      {calendarLoading && <div className="pcb-refresh">Refreshing calendar…</div>}
      <div className="pcb-calendar-block">
        <div className="pcb-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((w) => (
            <div key={w} className="pcb-weekday">{w}</div>
          ))}
        </div>
        <div className="pcb-grid" role="grid" aria-label="Choose a date">
          {gridDays.map((d, idx) => {
            const ymd = toLocalYMD(d);
            const active = ymd === toLocalYMD(selectedDate);
            const inMonth = d.getMonth() === monthAnchor.getMonth();
            const dMidnight = new Date(d); dMidnight.setHours(0, 0, 0, 0);
            const isPastDay = dMidnight.getTime() < todayMidnight.getTime();
            const isToday = ymd === todayYmd;
            const level = isPastDay ? 'none' : availabilityLevel(ymd);
            const count = heatmapMeta[ymd]?.count || 0;
            const classes = [
              'pcb-day',
              active && 'is-selected',
              !inMonth && 'is-outside',
              isPastDay && 'is-past',
              isToday && 'is-today',
              level === 'none' && !isPastDay && 'is-unavailable',
              level === 'limited' && 'is-limited',
              level === 'full' && 'is-full'
            ].filter(Boolean).join(' ');
            const statusLabel =
              level === 'open' ? `, ${count} slots`
                : level === 'limited' ? `, only ${count} slots left`
                  : level === 'full' ? ', fully booked'
                    : ', unavailable';
            return (
              <button
                key={idx}
                type="button"
                className={classes}
                disabled={isPastDay}
                aria-pressed={active}
                aria-label={`${d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}${statusLabel}`}
                onClick={() => {
                  if (isPastDay) return;
                  setSelectedDate(new Date(d));
                  setSelectedTime('');
                }}
              >
                <span className="pcb-day-num">{d.getDate()}</span>
                {!isPastDay && level !== 'none' ? (
                  <span className={`pcb-day-dot is-${level}`} aria-hidden="true" />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
      <div className="pcb-legend">
        <span><span className="pcb-legend-swatch" aria-hidden="true" /> Selected</span>
        <span><span className="pcb-legend-dot is-open" aria-hidden="true" /> Open</span>
        <span><span className="pcb-legend-dot is-limited" aria-hidden="true" /> Limited</span>
        <span><span className="pcb-legend-dot is-full" aria-hidden="true" /> Full</span>
      </div>
    </>
  );

  const durationBlock = (
    <div className="pcb-section">
      <label className="pcb-label">Duration</label>
      <div className="pcb-duration" role="group" aria-label="Meeting duration">
        {DURATION_PRESETS.map((m) => (
          <button
            key={m}
            type="button"
            className={`pcb-chip ${!isCustomDuration && meetingDuration === m ? 'is-active' : ''}`}
            onClick={() => selectDuration(m)}
            aria-pressed={!isCustomDuration && meetingDuration === m}
          >
            {m} min
          </button>
        ))}
        <button
          type="button"
          className={`pcb-chip pcb-chip-custom ${isCustomDuration ? 'is-active' : ''}`}
          onClick={() => setCustomDurationOpen(true)}
          aria-pressed={isCustomDuration}
        >
          Custom
        </button>
      </div>
      {isCustomDuration && (
        <div className="pcb-custom-duration">
          <input
            type="number"
            min={5}
            max={240}
            step={5}
            value={durationMins}
            onChange={(e) => {
              const v = Math.max(5, Math.min(240, Number(e.target.value || 0)));
              setMeetingDuration(v);
              setSelectedTime('');
            }}
            title="Custom duration (minutes)"
            aria-label="Custom duration in minutes"
          />
          <span>min</span>
        </div>
      )}
    </div>
  );

  const timeBlock = (
    <div className={`pcb-section pcb-time-section ${showErrors && !selectedTime ? 'pcb-slots-error' : ''}`}>
      <div className="pcb-time-head">
        <label className="pcb-label">
          Time{slotsForDate.length > 0 ? ` · ${slotsForDate.length} available` : ''}
        </label>
        <span className="pcb-tz" title="All times shown in this timezone">{tzLabel}</span>
      </div>
      {slotsForDate.length === 0 ? (
        <div className="pcb-empty">
          <strong>No available slots</strong>
          <p>
            Nothing open on this day for a {durationMins}-minute meeting.
            Try another date or a shorter duration.
          </p>
          <button
            type="button"
            className="pcb-empty-btn"
            onClick={() => {
              goToNextAvailable();
              goToStep(1);
            }}
          >
            Find next available day
          </button>
        </div>
      ) : (
        <div className="pcb-slots" role="listbox" aria-label="Available times">
          {slotsForDate.map((s) => {
            const isPastTime = toLocalYMD(selectedDate) === todayYmd && toMinutes(s) < nowMinutes;
            const active = selectedTime === s;
            return (
              <button
                key={s}
                type="button"
                role="option"
                className={`pcb-slot ${active ? 'is-active' : ''}`}
                aria-selected={active}
                disabled={isPastTime || calendarLoading}
                onClick={() => {
                  if (isPastTime || calendarLoading) return;
                  setSelectedTime(s);
                  setStep(3);
                  if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                title={`${formatTime12(s)} - ${formatTime12(addMinutesToHHMM(s, durationMins))}`}
              >
                {formatTime12(s)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const detailsBlock = (
    <>
      {!isSignedIn && (
        <div className={`pcb-google ${showErrors && !isSignedIn ? 'is-error' : ''}`}>
          <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setToast('Google login failed')} text="signin_with" shape="rectangular" size="large" />
        </div>
      )}
      {isSignedIn && (
        <>
          <div className="pcb-field">
            <label>Name</label>
            <input className="form-control" value={name || authName} onChange={(e) => setName(e.target.value)} disabled />
          </div>
          <div className="pcb-field-row">
            <div className="pcb-field">
              <label>Email</label>
              <input className="form-control" value={email || authEmail} onChange={(e) => setEmail(e.target.value)} disabled />
            </div>
            <div className="pcb-field">
              <label>Phone</label>
              <input
                className={`form-control ${showErrors && !phone ? 'is-error' : ''}`}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
                autoComplete="tel"
                placeholder="Your phone number"
              />
            </div>
          </div>
        </>
      )}
      {!isSignedIn && (
        <div className="pcb-field">
          <label>Phone</label>
          <input
            className={`form-control ${showErrors && !phone ? 'is-error' : ''}`}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputMode="tel"
            autoComplete="tel"
            placeholder="Your phone number"
          />
        </div>
      )}

      {(showExtras || !isMobile) ? (
        <>
          {isMobile && (
            <button type="button" className="pcb-extras-toggle" onClick={() => setShowExtras(false)}>
              Hide optional details
            </button>
          )}
          <div className="pcb-field">
            <label>Location (optional)</label>
            <div className="pcb-loc-row">
              <div>
                <input
                  className="form-control"
                  placeholder="Place name or Google Maps link"
                  value={location}
                  onChange={(e) => { setLocation(e.target.value); setLocQuery(e.target.value); }}
                  onFocus={() => setLocQuery((q) => q || location)}
                />
                {locLoading && <div className="pcb-searching">Searching…</div>}
                {locSuggestions.length > 0 && (
                  <div className="pcb-suggest">
                    {locSuggestions.map((sug, i) => (
                      <div
                        key={`${sug.place_id || i}`}
                        className="pcb-suggest-item"
                        onMouseDown={() => {
                          const addr = sug.display_name || '';
                          setLocation(addr);
                          setResolvedAddress(addr);
                          const lat = parseFloat(sug.lat); const lng = parseFloat(sug.lon);
                          if (Number.isFinite(lat) && Number.isFinite(lng)) setPin({ lat, lng });
                          setLocSuggestions([]);
                        }}
                      >
                        {sug.display_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="pcb-loc-btn"
                onClick={async () => {
                  try {
                    if (!navigator.geolocation) { setToast('Geolocation not supported'); return; }
                    navigator.geolocation.getCurrentPosition(async (pos) => {
                      const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                      setPin(coords);
                      try {
                        const resp = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&zoom=18&addressdetails=1`, { headers: { 'User-Agent': 'BloomNav' } });
                        const data = await resp.json();
                        const display = data?.display_name || '';
                        setResolvedAddress(display);
                        setLocation((prev) => prev || display);
                      } catch (_) { /* ignore */ }
                    }, (err) => setToast(err?.message || 'Unable to fetch location'), { enableHighAccuracy: true, timeout: 10000 });
                  } catch (_) {
                    setToast('Failed to get your location');
                  }
                }}
                title="Use current location"
                aria-label="Use current location"
              >
                Use current location
              </button>
            </div>
            {!!resolvedAddress && <div className="pcb-hint">{resolvedAddress}</div>}
          </div>
          <div className="pcb-field">
            <label>Message (optional)</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Anything you want to discuss…"
              value={agenda}
              onChange={(e) => setAgenda(e.target.value)}
            />
          </div>
          <div className="pcb-field">
            <label>Images (optional)</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                setUploadingImage(true);
                try {
                  const resized = await Promise.all(files.map(async (f) => {
                    const MAX_W = 1024; const MAX_H = 1024;
                    const TARGET_MB = 1;
                    if (f.size <= TARGET_MB * 1024 * 1024) return f;
                    const reader = new FileReader();
                    const dataUrl = await new Promise((res, rej) => {
                      reader.onload = () => res(reader.result);
                      reader.onerror = rej;
                      reader.readAsDataURL(f);
                    });
                    const img = await new Promise((res, rej) => {
                      const i = new Image();
                      i.onload = () => res(i);
                      i.onerror = rej;
                      i.src = dataUrl;
                    });
                    let w = img.width; let h = img.height;
                    if (w > h) {
                      if (w > MAX_W) { h = Math.round(h * (MAX_W / w)); w = MAX_W; }
                    } else if (h > MAX_H) {
                      w = Math.round(w * (MAX_H / h)); h = MAX_H;
                    }
                    const canvas = document.createElement('canvas');
                    canvas.width = w; canvas.height = h;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, w, h);
                    const blob = await new Promise((res) => canvas.toBlob(res, f.type, 0.7));
                    return new File([blob], f.name, { type: f.type, lastModified: Date.now() });
                  }));
                  const urls = [];
                  for (let i = 0; i < resized.length; i++) {
                    const file = resized[i];
                    const { data } = await api.get('/api/upload/presigned-url', { params: { fileType: file.type } });
                    const uploadUrl = data?.uploadUrl;
                    const fileUrl = data?.fileUrl;
                    if (!uploadUrl || !fileUrl) continue;
                    await axios.put(uploadUrl, file, {
                      headers: { 'Content-Type': file.type },
                      onUploadProgress: (evt) => {
                        const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
                        setUploadProgress((prev) => ({ ...prev, [i]: pct }));
                      }
                    });
                    urls.push(fileUrl);
                  }
                  if (urls.length) setImages((prev) => [...prev, ...urls]);
                } catch (_) {
                  setToast('Image upload failed');
                } finally {
                  setUploadingImage(false);
                }
              }}
              disabled={uploadingImage}
              className="form-control"
            />
            {uploadingImage && (
              <div style={{ marginTop: 8 }}>
                {Object.keys(uploadProgress).map((k) => (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <div className="progress-bar" style={{ flex: 1 }}>
                      <div className="progress-fill" style={{ width: `${uploadProgress[k]}%` }} />
                    </div>
                    <span className="pcb-hint">{uploadProgress[k]}%</span>
                  </div>
                ))}
              </div>
            )}
            {images.length > 0 && (
              <div className="pcb-thumbs" style={{ marginTop: 8 }}>
                {images.map((url, idx) => (
                  <div key={idx} className="pcb-thumb">
                    <img src={url} alt={`upload-${idx}`} />
                    <button
                      type="button"
                      onClick={() => setImages((prev) => prev.filter((_, i) => i !== idx))}
                      title="Remove"
                      aria-label="Remove image"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <button type="button" className="pcb-extras-toggle" onClick={() => setShowExtras(true)}>
          + Add location, message, or images
        </button>
      )}

      <div className="pcb-submit-wrap pcb-sticky-cta">
        <div className="pcb-confirm-row">
          <button type="button" className="pcb-btn pcb-btn-ghost" onClick={() => goToStep(2)}>
            <FiArrowLeft size={18} />
            Back
          </button>
          <button
            type="button"
            className={`pcb-btn pcb-btn-primary pcb-cta ${canConfirm ? 'is-ready' : ''}`}
            onClick={confirmBooking}
            disabled={submitting || uploadingImage || !canConfirm}
          >
            <span className="pcb-cta-copy">
              <span className="pcb-cta-main">{ctaMain}</span>
              <span className="pcb-cta-sub">{canConfirm ? ctaSub : 'Sign in and add your phone to finish'}</span>
            </span>
            {!submitting && <FiArrowRight size={18} className="pcb-cta-arrow" aria-hidden="true" />}
          </button>
        </div>
        {!!submitError && <div className="pcb-error">{submitError}</div>}
      </div>
    </>
  );

  return (
    <div className={`pcb page-content ${isMobile ? 'is-mobile' : 'is-desktop'}`}>
      {toast && <div className="pcb-toast" role="status">{toast}</div>}

      <header className="pcb-hero">
        <h1>{hostName ? `Book a meeting with ${hostName}` : 'Book a meeting'}</h1>
        <p>Pick a date and time that works for you.</p>
      </header>

      <nav className="pcb-wizard" aria-label="Booking steps">
        <div className="pcb-stepper">
          <div
            className="pcb-stepper-rail"
            aria-hidden="true"
          >
            <div
              className="pcb-stepper-rail-fill"
              style={{ width: `${((step - 1) / 2) * 100}%` }}
            />
          </div>
          {[
            { n: 1, label: 'Date' },
            { n: 2, label: 'Time' },
            { n: 3, label: 'Confirm' }
          ].map(({ n, label }) => (
            <div
              key={n}
              className={`pcb-stepper-step ${step === n ? 'is-current' : ''} ${step > n ? 'is-done' : ''}`}
              aria-current={step === n ? 'step' : undefined}
            >
              <span className="pcb-stepper-num" aria-hidden="true">{step > n ? '✓' : n}</span>
              <span className="pcb-stepper-label">{label}</span>
            </div>
          ))}
        </div>
      </nav>

      <div className="pcb-flow">
        <div className="pcb-card">
          <div className="pcb-card-head">
            <span className="pcb-card-title">
              {step === 1 && 'Pick a date'}
              {step === 2 && 'Duration & time'}
              {step === 3 && 'Your details'}
            </span>
            {step >= 2 && (
              <button
                type="button"
                className="pcb-head-meta"
                onClick={() => goToStep(selectedTime && step === 3 ? 2 : 1)}
                title={selectedTime ? 'Change date or time' : 'Change date'}
                aria-label={selectedTime ? 'Change date or time' : 'Change selected date'}
              >
                <span>
                  {selectedDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                  {selectedTime ? ` · ${formatTime12(selectedTime)}` : ''}
                </span>
                <FiEdit2 size={14} aria-hidden="true" />
              </button>
            )}
          </div>
          <div className="pcb-card-body">
            {step === 1 && (
              <div className="pcb-step-pane" key="step-date">
                {calendarGrid}
                <div className="pcb-wizard-actions pcb-sticky-cta">
                  <button
                    type="button"
                    className="pcb-btn pcb-btn-primary pcb-btn-block"
                    onClick={() => goToStep(2)}
                  >
                    Continue
                    <FiArrowRight size={18} aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="pcb-step-pane" key="step-time">
                {durationBlock}
                {timeBlock}
                <div className="pcb-wizard-actions pcb-wizard-actions-split pcb-sticky-cta">
                  <button type="button" className="pcb-btn pcb-btn-ghost" onClick={() => goToStep(1)}>
                    <FiArrowLeft size={18} />
                    Back
                  </button>
                  <button
                    type="button"
                    className="pcb-btn pcb-btn-primary"
                    disabled={!selectedTime || slotsForDate.length === 0}
                    onClick={() => {
                      if (slotsForDate.length === 0) {
                        setToast('No slots for this duration - try another');
                        return;
                      }
                      if (!selectedTime) {
                        setToast('Pick a time to continue');
                        return;
                      }
                      goToStep(3);
                    }}
                  >
                    Continue
                    <FiArrowRight size={18} aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="pcb-step-pane" key="step-details">
                {detailsBlock}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="pcb-footer">
        Powered by <a href="/" target="_blank" rel="noreferrer">BloomNav</a>
        <small>Unified Business Management</small>
      </div>
    </div>
  );
}
